import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomButton } from "../../../components/common/customButton";
import { CustomIconButton } from "../../../components/common/customIconButton";
import { ErrorDialog } from "../../../components/common/errorDialog";
import pencilIcon from "@/assets/svg/edit.svg";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { buildPipelineColumnDefs } from "../pipelineColumnDefs";
import { apiGet, apiPut } from "../../../services/genesisApi/httpsMethods";
import { exportLostDealsToExcel } from "../../../services/summaryExportService";
import type {
  Deal,
  KillReason,
  LostToLenderDetails,
  LostToLenderReason,
} from "../../../types/deal";
import {
  createNestedScalarColumn,
  createNestedSingleSelectColumn,
} from "../../../utils/columnHelpers";
import { prepareDealPutPayload } from "../../../utils/dealPutHelpers";
import { isGridCellValueEqual } from "../../../utils/compareData";
import {
  buildLostToLenderDetailsPayload,
  getLostToLenderNotes,
  getWinningLenderLtcLtv,
  hasLostDealRowEdits,
  hasLostToLenderCapture,
  isLostToOtherLenderStatus,
  killReasonsEqual,
  lostToLenderDetailsEqual,
} from "../../../utils/dealStatusHelpers";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

const LOST_DEALS_PIPELINE_COLUMNS = new Set([
  "deal_name",
  "loan_amount_fully_funded",
  "kill_reason",
]);

const LOST_DETAIL_SCALAR_FIELDS = [
  "notes",
  "winning_lender_name",
  "winning_lender_loan_amount",
  "winning_lender_margin",
  "winning_lender_ltc_ltv",
  "winning_lender_arrangement_fee",
  "winning_lender_initial_loan_term",
] as const satisfies readonly (keyof LostToLenderDetails)[];

type LostDetailScalarField = (typeof LOST_DETAIL_SCALAR_FIELDS)[number];

const isAccordionLike = (deal: Deal): boolean => {
  if (!deal || typeof deal !== "object") return false;
  return (
    typeof (deal as any).accordion_id === "number" ||
    typeof (deal as any).accordion_name === "string"
  );
};

const getLostReasonFromRow = (
  deal: Deal,
  reasons: LostToLenderReason[],
): LostToLenderReason | null => {
  const nested = deal.lost_to_lender_details?.lost_to_lender_reason;
  if (nested?.id != null) return nested;

  const reasonId = deal.lost_to_lender_details?.lost_to_lender_reason_id;
  if (typeof reasonId === "number") {
    return reasons.find((r) => r.id === reasonId) ?? null;
  }

  return null;
};

const patchLostDetailOnDeal = (
  deal: Deal,
  patch: Partial<LostToLenderDetails>,
) => {
  deal.lost_to_lender_details = buildLostToLenderDetailsPayload(
    deal.lost_to_lender_details,
    patch,
  );
};

const setLostReasonOnRow = (deal: Deal, reason: LostToLenderReason | null) => {
  patchLostDetailOnDeal(deal, { lost_to_lender_reason: reason });
};

const getLostDetail = <K extends keyof LostToLenderDetails>(
  deal: Deal,
  key: K,
): LostToLenderDetails[K] | undefined => deal.lost_to_lender_details?.[key];

export function LostDealsTab() {
  const { canEdit } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [initialDeals, setInitialDeals] = useState<Deal[]>([]);
  const [missingOnly, setMissingOnly] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const referenceLists = useReferenceLists();
  const {
    killReasons,
    lostToLenderReasons,
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    capitalSources,
  } = referenceLists;
  const killReasonList = killReasons ?? [];
  const lostReasonList = lostToLenderReasons ?? [];
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();

  const loadDeals = useCallback(() => {
    setIsLoading(true);
    return apiGet<Deal[]>("deals?active_only=false")
      .then((response) => {
        if (response.success && response.data) {
          setDeals(response.data);
          setInitialDeals(structuredClone(response.data));
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch deals. Please try again.",
        });
      })
      .finally(() => setIsLoading(false));
  }, [showErrorFromResponse]);

  useEffect(() => {
    void loadDeals();
    // Fetch once on mount; showErrorFromResponse is not stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lostDeals = useMemo(() => {
    return deals.filter((d) => {
      if (isAccordionLike(d)) return false;
      return isLostToOtherLenderStatus(d?.status?.status);
    });
  }, [deals]);

  const initialLostDeals = useMemo(() => {
    return initialDeals.filter((d) => {
      if (isAccordionLike(d)) return false;
      return isLostToOtherLenderStatus(d?.status?.status);
    });
  }, [initialDeals]);

  const missingDetailsCount = useMemo(
    () =>
      initialLostDeals.filter(
        (d) => !hasLostToLenderCapture(d.lost_to_lender_details),
      ).length,
    [initialLostDeals],
  );

  const filteredDeals = useMemo(() => {
    if (!missingOnly) return lostDeals;
    return lostDeals.filter((d) => {
      if (editMode) {
        const initial = initialDeals.find((row) => row.id === d.id);
        return (
          initial != null &&
          !hasLostToLenderCapture(initial.lost_to_lender_details)
        );
      }
      return !hasLostToLenderCapture(d.lost_to_lender_details);
    });
  }, [lostDeals, missingOnly, editMode, initialDeals]);

  const dirtyGrid = useMemo(
    () =>
      lostDeals.some((deal) => {
        const initial = initialDeals.find((row) => row.id === deal.id);
        return initial != null && hasLostDealRowEdits(deal, initial);
      }),
    [lostDeals, initialDeals],
  );

  const handleCancel = useCallback(() => {
    setDeals(structuredClone(initialDeals));
    setEditMode(false);
  }, [initialDeals]);

  const handleSave = useCallback(async () => {
    if (!dirtyGrid) {
      setEditMode(false);
      return;
    }

    const changedDeals = lostDeals.filter((deal) => {
      const initial = initialDeals.find((row) => row.id === deal.id);
      return initial != null && hasLostDealRowEdits(deal, initial);
    });

    if (changedDeals.length === 0) {
      setEditMode(false);
      return;
    }

    setSaving(true);
    try {
      for (const deal of changedDeals) {
        if (deal.id == null || typeof deal.id !== "number") continue;

        const initial = initialDeals.find((row) => row.id === deal.id);
        if (!initial) continue;

        const patches: {
          kill_reason?: KillReason | null;
          lost_to_lender_details?: LostToLenderDetails | null;
        } = {};

        if (!killReasonsEqual(deal.kill_reason, initial.kill_reason)) {
          patches.kill_reason = deal.kill_reason ?? null;
        }

        if (
          !lostToLenderDetailsEqual(
            deal.lost_to_lender_details,
            initial.lost_to_lender_details,
          )
        ) {
          patches.lost_to_lender_details = deal.lost_to_lender_details ?? null;
        }

        const response = await apiPut(
          "deal",
          deal.id,
          prepareDealPutPayload(deal, patches),
        );

        if (!response.success) {
          showErrorFromResponse(response);
          return;
        }
      }

      setInitialDeals(structuredClone(deals));
      setEditMode(false);
    } catch {
      showErrorFromResponse({
        success: false,
        status: 500,
        message: "Failed to save lost deal details. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  }, [
    deals,
    dirtyGrid,
    initialDeals,
    lostDeals,
    showError,
    showErrorFromResponse,
  ]);

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSave);

  const handleExportToExcel = useCallback(() => {
    setIsExporting(true);
    try {
      exportLostDealsToExcel({
        deals: filteredDeals,
        missingOnly,
      });
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to export lost deals. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [filteredDeals, missingOnly, showErrorFromResponse]);

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Deal>) => {
      if (!editMode) return;

      const { colDef, newValue, oldValue, data } = event;
      const field = colDef.field ?? colDef.colId;
      if (!data?.id || !field) return;

      if (isGridCellValueEqual(newValue, oldValue)) return;

      setDeals((prevDeals) =>
        prevDeals.map((deal) =>
          deal.id === data.id ? structuredClone(data) : deal,
        ),
      );
    },
    [editMode],
  );

  const columnDefs = useMemo((): ColDef<Deal>[] => {
    const pipelineCols: ColDef<Deal>[] = buildPipelineColumnDefs({
      editMode,
      lists: {
        condensedSponsors,
        sources,
        countries,
        sectors,
        status,
        users,
        loanTypes,
        riskClasses,
        securityTypes,
        transactionTypes,
        currencies,
        killReasons: killReasonList,
        capitalSources,
      },
    });

    const fromPipeline = pipelineCols
      .filter(
        (col) =>
          typeof col.field === "string" &&
          LOST_DEALS_PIPELINE_COLUMNS.has(col.field),
      )
      .map((col) =>
        col.field === "kill_reason"
          ? { ...col, editable: editMode }
          : { ...col, editable: false },
      );

    const dealNameCol = fromPipeline.find((c) => c.field === "deal_name");
    const loanCol = fromPipeline.find(
      (c) => c.field === "loan_amount_fully_funded",
    );
    const killCol = fromPipeline.find((c) => c.field === "kill_reason");

    const lostReasonCol = createNestedSingleSelectColumn({
      colId: "lost_to_lender_reason",
      label: "Lost Reason",
      values: lostReasonList,
      labelKey: "reason",
      width: 200,
      editable: editMode,
      allowNull: true,
      getValue: (data) => getLostReasonFromRow(data as Deal, lostReasonList),
      setValue: (data, value) => setLostReasonOnRow(data as Deal, value),
    }) as ColDef<Deal>;

    const detailScalarCol = (
      colId: LostDetailScalarField,
      label: string,
      width: number,
      useNumberEditor = false,
    ): ColDef<Deal> =>
      createNestedScalarColumn({
        colId,
        label,
        width,
        editable: editMode,
        useNumberEditor,
        getValue: (data) => getLostDetail(data as Deal, colId),
        setValue: (data, value) =>
          patchLostDetailOnDeal(data as Deal, { [colId]: value }),
      }) as ColDef<Deal>;

    const ltcLtvCol = createNestedScalarColumn({
      colId: "winning_lender_ltc_ltv",
      label: "LTC/LTV (%)",
      width: 110,
      editable: editMode,
      useNumberEditor: true,
      getValue: (data) =>
        getWinningLenderLtcLtv((data as Deal).lost_to_lender_details),
      setValue: (data, value) =>
        patchLostDetailOnDeal(data as Deal, {
          winning_lender_ltc_ltv:
            value == null || value === "" ? null : (value as number),
        }),
    }) as ColDef<Deal>;

    const notesCol = createNestedScalarColumn({
      colId: "notes",
      label: "Notes",
      width: 220,
      editable: editMode,
      getValue: (data) => {
        const notes = getLostToLenderNotes(
          (data as Deal).lost_to_lender_details,
        );
        return notes === "" ? null : notes;
      },
      setValue: (data, value) =>
        patchLostDetailOnDeal(data as Deal, {
          notes:
            value == null || value === "" ? null : String(value).trim() || null,
        }),
    }) as ColDef<Deal>;

    return [
      ...(dealNameCol ? [dealNameCol] : []),
      ...(loanCol ? [loanCol] : []),
      lostReasonCol,
      ...(killCol ? [killCol] : []),
      detailScalarCol("winning_lender_name", "Winning Lender", 160),
      detailScalarCol(
        "winning_lender_loan_amount",
        "Winning Loan Amount",
        150,
        true,
      ),
      detailScalarCol("winning_lender_margin", "Margin (%)", 110, true),
      ltcLtvCol,
      detailScalarCol(
        "winning_lender_arrangement_fee",
        "Arr. Fee (%)",
        110,
        true,
      ),
      detailScalarCol(
        "winning_lender_initial_loan_term",
        "Term (y)",
        130,
        true,
      ),
      notesCol,
    ];
  }, [
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    capitalSources,
    killReasonList,
    lostReasonList,
    editMode,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          padding: "20px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          Loading lost deals…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mt: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={missingOnly}
                onChange={(_, checked) => setMissingOnly(checked)}
                size="small"
                disabled={editMode}
              />
            }
            label="Missing lost reason only"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "13px",
                color: "#182737",
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              minHeight: 32,
            }}
          >
            {editMode ? (
              <>
                <CustomButton
                  text="Cancel"
                  handleButtonPress={handleCancel}
                  isButtonDisabled={saving}
                  solid={false}
                  widthOverride="100px"
                />
                <CustomButton
                  text={saving ? "Saving…" : "Save"}
                  handleButtonPress={requestSave}
                  isButtonDisabled={saving || (!dirtyGrid && !isCellEditing)}
                  solid
                  widthOverride="100px"
                />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    "& .MuiIconButton-root": { width: 32, height: 32 },
                  }}
                >
                  <CustomIconButton
                    icon={
                      <Box
                        component="img"
                        src={pencilIcon}
                        alt="Edit lost deals"
                        sx={{ width: 20, height: 20, display: "block" }}
                      />
                    }
                    onClick={() => {
                      if (!canEdit) return;
                      setEditMode(true);
                    }}
                    disabled={!canEdit || filteredDeals.length === 0}
                    tooltip="Edit"
                  />
                </Box>
                <CustomButton
                  text="Export to Excel"
                  handleButtonPress={handleExportToExcel}
                  isButtonDisabled={isExporting || filteredDeals.length === 0}
                  isLoading={isExporting}
                  isLoadingText="Exporting…"
                  solid={false}
                  widthOverride="140px"
                />
              </>
            )}
          </Box>
        </Box>

        <Typography sx={{ fontSize: "13px", color: "#182737" }}>
          {missingDetailsCount === 0 ? (
            <>All lost deals have lost-to-lender details recorded.</>
          ) : (
            <>
              <strong>{missingDetailsCount}</strong>{" "}
              {missingDetailsCount === 1 ? "deal" : "deals"} missing
              lost-to-lender details
              {editMode ? " — edit cells, then Save." : "."}
            </>
          )}
          {saving ? " Saving…" : ""}
          {!missingOnly ? (
            <>
              {" "}
              · Showing <strong>{filteredDeals.length}</strong> lost deals
            </>
          ) : null}
        </Typography>

        <Box sx={{ width: "100%", height: 650 }}>
          {filteredDeals.length === 0 ? (
            <Typography sx={{ fontSize: "14px", color: "#182737", py: 2 }}>
              {missingOnly
                ? "No lost deals are missing lost-to-lender details."
                : "No lost deals found."}
            </Typography>
          ) : (
            <div
              className="ag-theme-alpine pipeline-deals-grid"
              style={{ width: "100%", height: "100%" }}
            >
              <AgGridReact<Deal>
                {...gridEditProps}
                rowData={filteredDeals}
                columnDefs={columnDefs}
                onCellValueChanged={onCellValueChanged}
                rowHeight={27}
                singleClickEdit={true}
                getRowId={({ data }) => data?.id?.toString() ?? ""}
              />
            </div>
          )}
        </Box>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
